import React, { useState, useEffect } from 'react';
import { Clock, Users } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import CarDamageSelector from '../../shared/components/CarDamageSelector';
import { sanitizeDriver2, validateDriver2 } from '../utils/rentalValidation';
import { useCollection } from '../../../hooks/useCollection';

export default function RentalForm({ clients, cars, settings, onSave, onClose, initial }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const { data: staffList = [] } = useCollection('staff');

  const [data, setData] = useState({
    clientId: '', clientName: '', clientCin: '', clientPhone: '', clientPermis: '',
    carId: '', carName: '', carChassis: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '', dailyRate: 0, days: 0, totalAmount: 0,
    deposit: 0, status: 'active', notes: '',
    isOpenContract: false,
    hasDriver2: false,
    driver2Id: '',
    driver2Name: '',
    driver2Cin: '',
    driver2Permis: '',
    driver2PermisDate: '',
    driver2Phone: '',
    fuelLevel: 'full',      // 'full' | 'three_quarters' | 'half' | 'quarter' | 'empty'
    lavage: 'oui',          // 'oui' | 'non'
    exitTime: '',           // Heure de sortie
    paymentMethod: 'Espèces', // Mode de paiement
    damages: [],            // [{ zone, severity, note }] — vehicle damage annotations
    papers: {
      carteGrise: true,
      assurance: true,
      vignette: true,
      visiteTechnique: true,
      autorisation: false,
      contrat: true
    },
    // Preload existing values when editing/re-opening a rental.
    ...(initial || {})
  });

  // Auto-calc days and total
  useEffect(() => {
    if (!data.startDate) return;
    
    let days = 0;
    if (data.isOpenContract) {
      // Open contract: calculate from start to NOW
      const startTime = new Date(data.startDate).getTime();
      const nowTime = Date.now();
      const diffMs = nowTime - startTime;
      days = Math.max(1, Math.ceil(diffMs / 86400000));
    } else if (data.endDate) {
      // Closed contract: from start to end date
      days = Math.ceil((new Date(data.endDate) - new Date(data.startDate)) / 86400000);
      if (days < 1) days = 1;
    }
    
    setData(prev => ({ ...prev, days, totalAmount: days * prev.dailyRate }));
  }, [data.startDate, data.endDate, data.dailyRate, data.isOpenContract]);

  const submit = async () => {
    if (!data.clientId || !data.carId || !data.startDate) {
      return alert(t('select_rental_validation') || (isAr ? 'يرجى ملء جميع الحقول المطلوبة' : 'Veuillez remplir tous les champs requis'));
    }
    if (!data.isOpenContract && !data.endDate) {
      return alert(isAr
        ? 'يرجى تحديد تاريخ الانتهاء أو تفعيل العقد المفتوح'
        : 'Veuillez définir la date de fin ou activer le contrat ouvert');
    }

    // Second-driver: validate, then strip any data when the option is off so
    // nothing invalid can be submitted or saved.
    const driver2Check = validateDriver2(data, isAr);
    if (!driver2Check.valid) {
      return alert('⚠️ ' + driver2Check.error);
    }

    const payload = sanitizeDriver2({
      ...data,
      isOpenContract: !!data.isOpenContract,
      endDate: data.isOpenContract ? '' : data.endDate
    });

    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  return (
    <Modal onClose={onClose} title={t('new_rental')}>
      <div className="space-y-4">
        {/* Client Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('client_req')}</label>
          <select 
            value={data.clientId}
            onChange={e => {
              const c = clients.find(x => x.id === e.target.value);
              setData(prev => ({ ...prev, clientId: e.target.value, 
                clientName: c?.name || '',
                clientCin: c?.cin || '', 
                clientPhone: c?.phone || '', 
                clientPermis: c?.permis || '',
                clientAddress: c?.address || '',
                clientNationality: c?.nationality || 'Marocaine',
                clientBirthDate: c?.birthDate || '',
                clientProfession: c?.profession || '',
                clientPassport: c?.passport || '',
                clientPassportPlace: c?.passportPlace || '',
                clientPermisDate: c?.permisDate || '',
                clientPhone2: c?.phone2 || '',
                clientAddress2: c?.address2 || '',
                clientDocCniRecto: c?.docCniRecto || c?.docCni || '',
                clientDocCniVerso: c?.docCniVerso || '',
                clientDocPermisRecto: c?.docPermisRecto || c?.docPermis || '',
                clientDocPermisVerso: c?.docPermisVerso || '',
                clientDocPassport: c?.docPassport || ''
              }));
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">{t('choose_client')}</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{`${c.name} - ${c.phone}`}</option>
            ))}
          </select>
        </div>

        {/* Car Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('car_req')}</label>
          <select 
            value={data.carId}
            onChange={e => {
              const c = cars.find(x => x.id === e.target.value);
              setData(prev => ({ ...prev, carId: e.target.value,
                carName: c ? `${c.brand} ${c.model}` : '', // plate kept separately in carPlate
                carBrand: c?.brand || '',
                carModel: c?.model || '',
                carPlate: c?.plate || '',
                carChassis: c?.chassisNumber || '',
                kmStart: c?.km || 0,
                dailyRate: c?.dailyRate || 0 
              }));
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">{t('choose_car')}</option>
            {cars.filter(c => c.status === 'available').map(c => (
              <option key={c.id} value={c.id}>
                {`${c.brand} ${c.model} - ${c.plate} (${c.dailyRate} ${settings.currency}/${isAr ? 'يوم' : 'j'})`}
              </option>
            ))}
          </select>
        </div>
        
        {/* Staff assignment (drives the commission) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            👔 {isAr ? 'الموظف المسؤول (عمولة)' : 'Employé assigné (commission)'}
          </label>
          <select
            value={data.staffId || ''}
            onChange={e => {
              const s = staffList.find(x => x.id === e.target.value);
              setData(prev => ({ ...prev, staffId: e.target.value, staffName: s ? s.name : '' }));
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">{isAr ? 'بدون موظف' : 'Aucun employé'}</option>
            {staffList.filter(s => s.status !== 'inactive').map(s => (
              <option key={s.id} value={s.id}>
                {`${s.name}${s.role ? ' — ' + s.role : ''}${s.salaryType === 'commission' ? ` (${s.commissionPerRental ?? 25} ${settings.currency})` : ''}`}
              </option>
            ))}
          </select>
        </div>

        {/* OPEN CONTRACT TOGGLE */}
        <div className={`p-4 rounded-xl border-2 transition ${data.isOpenContract ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'}`}>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!data.isOpenContract}
              onChange={e => setData(prev => ({ ...prev, isOpenContract: e.target.checked, endDate: e.target.checked ? '' : prev.endDate }))}
              className="w-5 h-5 mt-0.5 cursor-pointer accent-purple-600"
            />
            <div className="flex-1">
              <div className="font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-purple-650" />
                <span>{isAr ? '⏳ عقد مفتوح (Maftoh)' : '⏳ Contrat ouvert (Maftoh)'}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {isAr 
                  ? 'بدون تاريخ انتهاء محدد. كل 24 ساعة تضاف يوم واحد إلى الفاتورة تلقائياً.'
                  : 'Sans date de fin fixe. Chaque 24h écoulée ajoute 1 jour au total automatiquement.'}
              </div>
              {data.isOpenContract && data.startDate && (
                <div className="mt-3 p-3 bg-purple-100/50 rounded-lg border border-purple-200">
                  <div className="text-xs font-bold text-purple-900 mb-1">
                    {isAr ? '📊 الحساب الحالي:' : '📊 Calcul actuel:'}
                  </div>
                  <div className="text-sm text-purple-800">
                    {isAr 
                      ? `منذ ${data.startDate}: ${data.days} يوم × ${data.dailyRate} ${settings.currency} = `
                      : `Depuis ${data.startDate}: ${data.days} jour(s) × ${data.dailyRate} ${settings.currency} = `}
                    <span className="font-bold text-purple-900">{`${data.totalAmount} ${settings.currency}`}</span>
                  </div>
                  <div className="text-xs text-purple-700 mt-1">
                    {isAr 
                      ? '⚡ المبلغ يتم تحديثه تلقائياً كل يوم'
                      : '⚡ Le montant est recalculé automatiquement chaque jour'}
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field 
            label={isAr ? 'تاريخ البدء *' : 'Date début *'} 
            type="date" 
            value={data.startDate} 
            onChange={v => setData(prev => ({ ...prev, startDate: v }))} 
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {data.isOpenContract 
                ? (isAr ? 'تاريخ الانتهاء (مفتوح)' : 'Date fin (ouverte)')
                : (isAr ? 'تاريخ الانتهاء *' : 'Date fin *')}
            </label>
            <input
              type="date"
              value={data.endDate}
              onChange={e => setData(prev => ({ ...prev, endDate: e.target.value }))}
              disabled={data.isOpenContract}
              className={`w-full px-4 py-3 text-base border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                data.isOpenContract ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : ''
              }`}
            />
          </div>
          <Field 
            label={`${t('price_day') || 'Tarif journalier'} (${settings.currency})`} 
            type="number" 
            value={data.dailyRate}
            onChange={v => setData(prev => ({ ...prev, dailyRate: parseFloat(v) || 0 }))} 
          />
          <Field label={t('days_label')} type="number" value={data.days} disabled />
          <Field label={`${t('total_label')} (${settings.currency})`} type="number" value={data.totalAmount} disabled />
          <Field 
            label={`${t('deposit_label')} (${settings.currency})`} 
            type="number" 
            value={data.deposit}
            onChange={v => setData(prev => ({ ...prev, deposit: parseFloat(v) || 0 }))} 
          />
        </div>
        
        {/* VEHICLE PAPERS */}
        <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
          <h3 className="font-bold text-cyan-900 flex items-center gap-2 mb-3 text-sm">
            <span>📋</span> {isAr ? 'وثائق السيارة المرفقة' : 'Papiers du véhicule fournis'}
          </h3>
          <p className="text-xs text-cyan-700 mb-3">
            {isAr 
              ? 'حدد الوثائق المرفقة مع السيارة (ستظهر في العقد والفاتورة)'
              : 'Cochez les documents fournis avec le véhicule (apparaîtront dans le contrat et facture)'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { key: 'carteGrise', fr: 'Carte Grise', ar: 'البطاقة الرمادية', icon: '📄' },
              { key: 'assurance', fr: 'Assurance', ar: 'التأمين', icon: '🛡️' },
              { key: 'vignette', fr: 'Vignette', ar: 'الضريبة', icon: '🏷️' },
              { key: 'visiteTechnique', fr: 'Visite Technique', ar: 'الفحص التقني', icon: '🔍' },
              { key: 'autorisation', fr: 'Autorisation', ar: 'الترخيص', icon: '📜' },
              { key: 'contrat', fr: 'Contrat', ar: 'العقد', icon: '📝' }
            ].map(paper => (
              <label 
                key={paper.key}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 cursor-pointer transition select-none ${
                  data.papers[paper.key]
                    ? 'bg-green-50 border-green-400 shadow-sm text-green-950'
                    : 'bg-white border-gray-200 hover:border-cyan-300 text-gray-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!data.papers[paper.key]}
                  onChange={(e) => setData(prev => ({ 
                    ...prev, 
                    papers: { ...prev.papers, [paper.key]: e.target.checked }
                  }))}
                  className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500 cursor-pointer flex-shrink-0"
                />
                <span className="flex-shrink-0">{paper.icon}</span>
                <span className="text-xs font-semibold">{isAr ? paper.ar : paper.fr}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-3 text-xs">
            <button
              type="button"
              onClick={() => setData(prev => ({ 
                ...prev, 
                papers: { carteGrise: true, assurance: true, vignette: true, visiteTechnique: true, autorisation: true, contrat: true }
              }))}
              className="flex-1 px-3 py-1.5 bg-green-105 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg font-bold transition"
            >
              ✅ {isAr ? 'تحديد الكل' : 'Tout cocher'}
            </button>
            <button
              type="button"
              onClick={() => setData(prev => ({ 
                ...prev, 
                papers: { carteGrise: false, assurance: false, vignette: false, visiteTechnique: false, autorisation: false, contrat: false }
              }))}
              className="flex-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-800 rounded-lg font-bold transition"
            >
              ✕ {isAr ? 'إزالة الكل' : 'Tout décocher'}
            </button>
          </div>
        </div>
        
        {/* CAR STATE AND PAYMENT METHOD */}
        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl text-gray-900">
          <h3 className="font-bold text-orange-950 flex items-center gap-2 mb-3 text-sm">
            <span>⛽</span> {isAr ? 'حالة السيارة عند التسليم' : 'État du véhicule à la sortie'}
          </h3>
          <p className="text-xs text-orange-700 mb-4">
            {isAr 
              ? 'هذه المعلومات ستظهر تلقائياً في عقد الكراء'
              : 'Ces informations apparaîtront automatiquement dans le contrat'}
          </p>
          
          {/* Fuel Level */}
          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-700 mb-2">
              ⛽ {isAr ? 'مستوى الوقود عند التسليم' : '⛽ Niveau du carburant à la sortie'}
            </label>
            <div className="grid grid-cols-5 gap-1 sm:gap-2">
              {[
                { value: 'empty',           label: 'Vide',    labelAr: 'فارغ',     icon: '🔴', pct: 0   },
                { value: 'quarter',         label: '1/4',     labelAr: '1/4',      icon: '🟠', pct: 25  },
                { value: 'half',            label: '1/2',     labelAr: '1/2',      icon: '🟡', pct: 50  },
                { value: 'three_quarters',  label: '3/4',     labelAr: '3/4',      icon: '🟢', pct: 75  },
                { value: 'full',            label: 'Plein',   labelAr: 'ممتلئ',    icon: '✅', pct: 100 }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setData(prev => ({ ...prev, fuelLevel: opt.value }))}
                  className={`p-1.5 sm:p-2.5 rounded-lg border-2 transition cursor-pointer flex flex-col items-center gap-1 ${
                    data.fuelLevel === opt.value 
                      ? 'bg-orange-500 border-orange-700 text-white shadow-md' 
                      : 'bg-white border-gray-300 hover:border-orange-350 hover:border-orange-300 text-gray-700'
                  }`}
                >
                  <span className="text-lg">{opt.icon}</span>
                  <span className="text-[10px] font-bold whitespace-nowrap">{isAr ? opt.labelAr : opt.label}</span>
                  <span className={`text-[9px] ${data.fuelLevel === opt.value ? 'text-orange-100' : 'text-gray-400'}`}>{opt.pct}%</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Lavage */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">
                🧽 {isAr ? 'الغسيل' : 'Lavage'}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setData(prev => ({ ...prev, lavage: 'oui' }))}
                  className={`flex-1 py-2 rounded-lg border-2 text-xs font-bold transition ${
                    data.lavage === 'oui'
                      ? 'bg-green-500 border-green-700 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-green-300'
                  }`}
                >
                  OUI
                </button>
                <button
                  type="button"
                  onClick={() => setData(prev => ({ ...prev, lavage: 'non' }))}
                  className={`flex-1 py-2 rounded-lg border-2 text-xs font-bold transition ${
                    data.lavage === 'non'
                      ? 'bg-red-500 border-red-700 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-red-300'
                  }`}
                >
                  NON
                </button>
              </div>
            </div>
            
            {/* Heure de sortie */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">
                🕐 {isAr ? 'ساعة التسليم' : 'Heure de sortie'}
              </label>
              <input
                type="time"
                value={data.exitTime || ''}
                onChange={e => setData(prev => ({ ...prev, exitTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>
            
            {/* Payment Method */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">
                💳 {isAr ? 'طريقة الدفع' : 'Mode de paiement'}
              </label>
              <select
                value={data.paymentMethod || 'Espèces'}
                onChange={e => setData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-orange-300"
              >
                <option value="Espèces">{isAr ? 'نقداً' : 'Espèces'}</option>
                <option value="Chèque">{isAr ? 'شيك' : 'Chèque'}</option>
                <option value="Virement">{isAr ? 'تحويل بنكي' : 'Virement'}</option>
                <option value="Carte">{isAr ? 'بطاقة' : 'Carte bancaire'}</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* VEHICLE DAMAGE ANNOTATION */}
        <CarDamageSelector
          value={data.damages}
          onChange={(damages) => setData(prev => ({ ...prev, damages }))}
          isAr={isAr}
        />

        {/* SECOND DRIVER DETAILS */}
        <div className={`mt-4 p-4 rounded-xl border-2 transition ${data.hasDriver2 ? 'bg-indigo-50 border-indigo-300' : 'bg-gray-50 border-gray-200'}`}>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!data.hasDriver2}
              onChange={e => setData(prev => ({ 
                ...prev, 
                hasDriver2: e.target.checked,
                ...(e.target.checked ? {} : {
                  driver2Id: '', driver2Name: '', driver2Cin: '', 
                  driver2Permis: '', driver2PermisDate: '', driver2Phone: ''
                })
              }))}
              className="w-5 h-5 mt-0.5 cursor-pointer accent-indigo-650"
            />
            <div className="flex-1 text-gray-950">
              <div className="font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-650" />
                <span>{isAr ? '👥 سائق إضافي (Autre Conducteur)' : '👥 Autre Conducteur'}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {isAr 
                  ? 'إضافة سائق ثاني مسموح له بقيادة السيارة. سيظهر تلقائياً في عقد الكراء.'
                  : 'Ajouter un deuxième conducteur autorisé. Apparaîtra automatiquement dans le contrat.'}
              </div>
            </div>
          </label>
          
          {data.hasDriver2 && (
            <div className="mt-4 space-y-3">
              {/* Existing Client Selector */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {isAr ? 'اختيار من العملاء الموجودين (اختياري)' : 'Choisir parmi les clients existants (optionnel)'}
                </label>
                <select
                  value={data.driver2Id || ''}
                  onChange={e => {
                    if (!e.target.value) {
                      setData(prev => ({ ...prev, driver2Id: '' }));
                      return;
                    }
                    const c = clients.find(x => x.id === e.target.value);
                    if (c) {
                      setData(prev => ({ 
                        ...prev, 
                        driver2Id: c.id,
                        driver2Name: c.name || '',
                        driver2Cin: c.cin || '',
                        driver2Permis: c.permis || '',
                        driver2PermisDate: c.permisDate || '',
                        driver2Phone: c.phone || ''
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                >
                  <option value="">{isAr ? '— إدخال يدوي —' : '— Saisie manuelle —'}</option>
                  {clients
                    .filter(c => c.id !== data.clientId)
                    .map(c => (
                      <option key={c.id} value={c.id}>{`${c.name} - ${c.phone}`}</option>
                    ))}
                </select>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field 
                  label={isAr ? 'الإسم الكامل' : 'Nom et Prénom'} 
                  value={data.driver2Name} 
                  onChange={v => setData(prev => ({ ...prev, driver2Name: v }))}
                  placeholder={isAr ? 'محمد العلوي' : 'Mohamed Alaoui'} 
                />
                <Field 
                  label={isAr ? 'رقم ب ت و' : 'CIN N°'} 
                  value={data.driver2Cin} 
                  onChange={v => setData(prev => ({ ...prev, driver2Cin: v }))}
                  placeholder="AB123456" 
                />
                <Field 
                  label={isAr ? 'رقم رخصة السياقة' : 'N° Permis de conduire'} 
                  value={data.driver2Permis} 
                  onChange={v => setData(prev => ({ ...prev, driver2Permis: v }))} 
                />
                <Field 
                  label={isAr ? 'تاريخ إصدار الرخصة' : 'Délivré le'} 
                  type="date"
                  value={data.driver2PermisDate} 
                  onChange={v => setData(prev => ({ ...prev, driver2PermisDate: v }))} 
                />
                <div className="col-span-2">
                  <Field 
                    label={isAr ? 'الهاتف' : 'Téléphone'} 
                    value={data.driver2Phone} 
                    onChange={v => setData(prev => ({ ...prev, driver2Phone: v }))}
                    placeholder="06 12 34 56 78" 
                  />
                </div>
              </div>
              
              <div className="p-2 bg-amber-50 border border-amber-250 rounded-lg text-xs text-amber-800 flex items-start gap-2 select-none">
                <span>⚠️</span>
                <div>
                  {isAr 
                    ? 'السائق الإضافي يجب أن يكون لديه رخصة سياقة سارية المفعول. كلا السائقين مسؤولان عن السيارة.'
                    : 'Le 2ème conducteur doit avoir un permis de conduire valide. Les deux conducteurs sont responsables du véhicule.'}
                </div>
              </div>
            </div>
          )}

          {/* Disabled / read-only state when no second driver is selected */}
          {!data.hasDriver2 && (
            <div className="mt-3 px-4 py-3 bg-gray-100 border border-dashed border-gray-300 rounded-lg flex items-center gap-2 select-none">
              <span className="text-gray-400 text-base">🔒</span>
              <input
                type="text"
                readOnly
                disabled
                tabIndex={-1}
                aria-disabled="true"
                value={isAr ? 'لا يوجد سائق إضافي' : 'Aucun deuxième chauffeur — Non renseigné'}
                className="flex-1 bg-transparent text-sm italic text-gray-500 outline-none cursor-not-allowed border-none"
              />
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-6 border-t border-gray-150 pt-4">
          <button 
            type="button"
            onClick={onClose} 
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-bold hover:bg-gray-50 text-gray-700 text-sm transition"
          >
            {t('cancel_btn')}
          </button>
          <button 
            type="button"
            onClick={submit} 
            className={`flex-1 px-4 py-3 text-white rounded-xl font-bold shadow transition text-sm ${
              data.isOpenContract ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {t('save_btn')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
